import { useEffect, useRef, useState } from 'react'
import { SectionHeader } from '@/components/marketing/section'
import { TextLink } from '@/components/marketing/text-link'
import { ScrollGlobeStage } from '@/components/marketing/scroll-globe-stage'
import { AlertStack } from '@/components/marketing/alert-stack'
import { InlinePiece } from '@/components/marketing/inline-piece'
import { AlertToast, RegionConsensusPiece, UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { browserPoints, showcase } from './content'
import { DetectionBars } from './detection-bars'
import { FailureTable } from './failure-table'
import { RegionChecks } from './region-checks'
import { RunDetailFrame } from './run-detail-frame'
import { PromoVideo } from './promo-video'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'
import { MOBILE_APP, MOBILE_APP_VIDEO_URL } from '@/lib/mobile-app'

// Scroll story: four chapters on the right, one sticky 3D stage on the left. The
// chapters hold all the content, so the page reads in full without JS and without
// the stage. Below `lg` there is no stage; each chapter shows its own piece inline.

const CHAPTERS = 4

/** The chapter that crosses the middle of the viewport. */
function useActiveChapter() {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [active, setActive] = useState(0)
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.chapter ?? 0))
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    )
    root.querySelectorAll('[data-chapter]').forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
  return { rootRef, active }
}

function Chapter({ index, id, children }: { index: number; id?: string; children: React.ReactNode }) {
  return (
    <article id={id} data-chapter={index} className="scroll-mt-24 py-12 lg:flex lg:min-h-[85vh] lg:flex-col lg:justify-center">
      {children}
    </article>
  )
}

const stagePieces = [
  <div key="toast" className="flex h-full items-center justify-center bg-background">
    <AlertToast />
  </div>,
  <RegionConsensusPiece key="consensus" />,
  <UptimeBarsPiece key="uptime" />,
  <div key="alerts" className="flex h-full items-center p-2">
    <AlertStack compact className="w-full" />
  </div>,
]

export function Story() {
  const { rootRef, active } = useActiveChapter()
  return (
    <section>
      <div ref={rootRef} className="container mx-auto grid grid-cols-1 gap-x-12 px-4 py-6 md:py-12 lg:grid-cols-12">
        <div className="hidden lg:col-span-6 lg:block">
          <div className="sticky top-24 h-[calc(100vh-8rem)] max-h-[680px] min-h-[460px]">
            <ScrollGlobeStage active={Math.min(active, CHAPTERS - 1)} pieces={stagePieces} />
          </div>
        </div>
        <div className="lg:col-span-6">
          <Chapter index={0}>
            <SectionHeader
              title="Check the whole flow, not just the home page"
              lead="A ping says the server answered. A browser check says a real user could actually log in and check out, with a screenshot of every step."
            />
            <ul className="mt-8 space-y-5">
              {browserPoints.map((p) => (
                <li key={p.title} className="border-l-2 border-border pl-4">
                  <h3 className="font-semibold text-foreground">{p.title}</h3>
                  <p className="mt-1 text-[15px] text-body">{p.desc}</p>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <RunDetailFrame />
            </div>
            <div className="mt-7">
              <TextLink to="/features/browser-monitoring">How browser monitoring works</TextLink>
            </div>
          </Chapter>

          <Chapter index={1} id="how-it-works">
            <SectionHeader
              title="Only confirmed outages page you"
              lead={`Most monitors alert on the first failed check. Uptrack requires consecutive failures. ${MONITOR_LOCATIONS.story} The free plan checks from Nuremberg and still waits for consecutive failures.`}
            />
            <p className="mt-4 text-[15px] leading-relaxed text-body">{showcase[0].desc}</p>
            <InlinePiece>
              <RegionConsensusPiece />
            </InlinePiece>
            <FailureTable className="mt-6" />
            <RegionChecks className="mt-6" />
            <div className="mt-6">
              <TextLink to="/blog/22-check-locations">How consensus works</TextLink>
            </div>
          </Chapter>

          <Chapter index={2}>
            <SectionHeader
              title="Why 30 seconds matters"
              lead="Most free plans elsewhere check every 5 minutes. Your site could be down for 4 minutes and 59 seconds before you know. Uptrack checks every 30 seconds on every plan, including free, and paid plans start at $3/mo."
            />
            <div className="mt-8">
              <DetectionBars />
            </div>
            <h3 className="mt-8 text-[17px] font-semibold text-foreground">{showcase[1].title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-body">{showcase[1].desc}</p>
            <InlinePiece>
              <UptimeBarsPiece />
            </InlinePiece>
          </Chapter>

          <Chapter index={3}>
            <SectionHeader title={showcase[2].title} lead={showcase[2].desc} />
            <InlinePiece>
              <div className="flex h-full items-center p-2">
                <AlertStack compact className="w-full" />
              </div>
            </InlinePiece>
            {MOBILE_APP.promoVideoId && (
              <PromoVideo
                src={MOBILE_APP_VIDEO_URL}
                caption="See an alert reach your phone. App preview, about 45 seconds."
                className="mt-8"
              />
            )}
          </Chapter>
        </div>
      </div>
    </section>
  )
}
